import React, { useEffect, useRef } from "react";
import { siteData, contactData } from "../data/mock";
import { ChevronDown, Instagram, Phone, Mail } from "lucide-react";
import { Button } from "./ui/button";

const Hero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 20;
      heroRef.current.style.setProperty("--mouse-x", `${x}px`);
      heroRef.current.style.setProperty("--mouse-y", `${y}px`);
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const scrollToAbout = () => {
    const el = document.getElementById("about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="hero" ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        {/* Gradient orbs */}
        <div
          className="absolute top-1/4 -left-32 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "4s" }}
        />
        <div
          className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "6s" }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-600/10 rounded-full blur-3xl"
          style={{
            transform: `translate(calc(-50% + var(--mouse-x, 0px)), calc(-50% + var(--mouse-y, 0px)))`,
          }}
        />
        {/* Subtle wave pattern overlay */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 1440 900" preserveAspectRatio="none">
            <path
              d="M0,300 C360,200 720,400 1080,300 C1260,250 1380,350 1440,300 L1440,900 L0,900Z"
              fill="currentColor"
              className="text-teal-400"
            />
            <path
              d="M0,500 C360,400 720,600 1080,500 C1260,450 1380,550 1440,500 L1440,900 L0,900Z"
              fill="currentColor"
              className="text-cyan-400"
              opacity="0.5"
            />
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        {/* Decorative line */}
        <div className="flex items-center justify-center gap-4 mb-8 animate-fade-in-up">
          <span className="w-12 h-px bg-teal-700/60" />
          <span className="font-nunito text-teal-400 text-sm tracking-[0.3em] uppercase">{siteData.nickname}</span>
          <span className="w-12 h-px bg-teal-700/60" />
        </div>

        {/* Main title */}
        <h1 className="font-bebas text-7xl sm:text-8xl md:text-9xl lg:text-[10rem] leading-none tracking-wider text-black mb-6 animate-fade-in-up animation-delay-200">
          <span className="relative inline-block">
            AURIE
            <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-teal-600 to-transparent" />
          </span>
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-900 via-cyan-700 to-teal-900">
            CANO
          </span>
        </h1>

        {/* Tagline */}
        <p className="font-nunito text-lg md:text-xl text-slate-200 max-w-2xl mx-auto mb-4 animate-fade-in-up animation-delay-400">
          {siteData.tagline}
        </p>
        <p className="font-nunito text-base md:text-lg text-teal-400/80 max-w-xl mx-auto mb-10 italic animate-fade-in-up animation-delay-600">
          {siteData.heroSubtitle}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 animate-fade-in-up animation-delay-800">
          <a href={`tel:${contactData.whatsapp.replace(/\s/g, "")}`}>
            <Button className="bg-teal-700 hover:bg-teal-600 text-white font-nunito font-bold text-base px-8 py-6 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-teal-700/30 hover:-translate-y-0.5">
              <Phone className="w-5 h-5 mr-2" />
              Contactar
            </Button>
          </a>
          <Button
            variant="outline"
            onClick={scrollToAbout}
            className="border-slate-400 text-slate-100 hover:bg-slate-200/50 hover:border-teal-400/50 font-nunito font-medium text-base px-8 py-6 rounded-full transition-all duration-300"
          >
            Conocer más
          </Button>
        </div>

        {/* Social links */}
        <div className="flex items-center justify-center gap-6 animate-fade-in-up animation-delay-1000">
          <a
            href={contactData.instagram}
            onClick={(e) => {
              e.preventDefault();
              window.open(contactData.instagram, "_blank", "noopener,noreferrer");
            }}
            className="group p-3 rounded-full border border-slate-400/40 hover:border-teal-600/50 hover:bg-teal-600/10 transition-all duration-300"
          >
            <Instagram className="w-5 h-5 text-slate-200 group-hover:text-teal-300 transition-colors duration-300" />
          </a>
          <a
            href={contactData.tiktok}
            onClick={(e) => {
              e.preventDefault();
              window.open(contactData.tiktok, "_blank", "noopener,noreferrer");
            }}
            className="group p-3 rounded-full border border-slate-400/40 hover:border-teal-600/50 hover:bg-teal-600/10 transition-all duration-300"
          >
            <svg
              className="w-5 h-5 text-slate-200 group-hover:text-teal-300 transition-colors duration-300"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.86a8.28 8.28 0 004.76 1.5v-3.4a4.85 4.85 0 01-1-.27z" />
            </svg>
          </a>
          <a
            href={`mailto:${contactData.email}`}
            className="group p-3 rounded-full border border-slate-400/40 hover:border-teal-600/50 hover:bg-teal-600/10 transition-all duration-300"
          >
            <Mail className="w-5 h-5 text-slate-200 group-hover:text-teal-300 transition-colors duration-300" />
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollToAbout}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-400 hover:text-teal-300 transition-colors duration-300 animate-bounce"
      >
        <ChevronDown className="w-8 h-8" />
      </button>
    </section>
  );
};

export default Hero;
